import { useState } from "react";
import type { PlayerResponse } from "@softmax/observatory-client/types";
import { usePlayerMutation } from "@/features/players/commands";
import type { AccountWrites } from "./use-account-writes";
import { agentRows, type AgentRow } from "./agent-rows";

export function useAgentEditor({
  agents,
  writes,
}: {
  agents: PlayerResponse[];
  writes: AccountWrites;
}) {
  const mutation = usePlayerMutation({ elevatedPrivileges: false });
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [newName, setNewName] = useState("");
  const rows = agentRows({ agents, drafts, newName });

  function setName({ row, value }: { row: AgentRow; value: string | null }) {
    writes.note({
      key: `agent-name-${row.id ?? "new"}`,
      notice: null,
    });

    if (row.id === null) {
      setNewName(value ?? "");
      return;
    }

    const id = row.id;
    setDrafts((previous) => {
      const next = { ...previous };

      if (value === null || value === row.savedName) delete next[id];
      else next[id] = value;

      return next;
    });
  }

  async function saveName(row: AgentRow) {
    const key = `agent-name-${row.id ?? "new"}`;
    const playerName = row.name.trim();

    if (playerName === "") {
      writes.note({
        key,
        notice: { tone: "error", text: "Your agent needs a name." },
      });
      return false;
    }

    if (playerName === row.savedName) {
      setName({
        row,
        value: null,
      });
      return true;
    }

    return writes.run({
      key,
      progress: row.player ? "Saving…" : "Creating…",
      done: "Saved",
      work: async () => {
        const saved = await mutation.mutateAsync(
          row.player
            ? { kind: "rename", player: row.player, name: playerName }
            : { kind: "create", name: playerName },
        );
        setName({
          row,
          value: null,
        });
        writes.note({
          key: `agent-name-${saved.id}`,
          notice: { tone: "saved", text: "Saved" },
        });
      },
    });
  }

  function updatePhoto({
    player,
    file,
  }: {
    player: PlayerResponse;
    file: File | null;
  }) {
    const key = `agent-row-${player.id}`;

    if (
      file &&
      !writes.acceptable({
        key,
        file,
      })
    )
      return;

    void writes.run({
      key,
      progress: file ? "Uploading…" : "Removing…",
      done: file ? "Icon updated." : "Icon removed.",
      work: async () => {
        await mutation.mutateAsync(
          file
            ? { kind: "upload-avatar", player, file }
            : { kind: "remove-avatar", player },
        );
      },
    });
  }
  function makeDefault(player: PlayerResponse) {
    void writes.run({
      key: `agent-row-${player.id}`,
      progress: `Making ${player.name} the default…`,
      done: `${player.name} is now your default agent.`,
      work: async () => {
        await mutation.mutateAsync({ kind: "default", player });
      },
    });
  }

  return {
    rows,
    newName,
    hasSavedAgents: agents.length > 0,
    setName,
    saveName,
    updatePhoto,
    makeDefault,
    discardNewName: () => setNewName(""),
    unsaved: Object.keys(drafts).length > 0 || newName !== "",
  };
}
